import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
// Fixed sections removed — use '+ Add section' instead
import { CoeService } from "../../../services/dataService";

import AlteryxLogo from "../../../assets/imgs/AcademyLogo/Alteryx_logo.svg";
import AWSLogo from "../../../assets/imgs/AcademyLogo/Amazon_Web_Services_Logo.svg";
import BluePrismLogo from "../../../assets/imgs/AcademyLogo/Blue_Prism_logo.svg";
import CelonisLogo from "../../../assets/imgs/AcademyLogo/Celonis_Logo.svg";
import FortinetLogo from "../../../assets/imgs/AcademyLogo/Fortinet_logo.svg";
import GoogleLogo from "../../../assets/imgs/AcademyLogo/Google_Developers_ogo.svg";
import JuniperLogo from "../../../assets/imgs/AcademyLogo/Juniper_Networks_logo.svg";
import MicrochipLogo from "../../../assets/imgs/AcademyLogo/Microchip_logo.svg";
import PaloAltoLogo from "../../../assets/imgs/AcademyLogo/PaloAltoNetworks_Logo.svg";
import UiPathLogo from "../../../assets/imgs/AcademyLogo/UiPath_logo.svg";
import ZscalerLogo from "../../../assets/imgs/AcademyLogo/Zscaler_logo.svg";
import BentleyLogo from "../../../assets/imgs/AcademyLogo/bentley-systems-logo.svg";
import AltairLogo from "../../../assets/imgs/AcademyLogo/Altair_logo.svg";
import EduSkillsLogo from "../../../assets/imgs/AcademyLogo/eduskills-academy-logo.png";
import AnsycLogo from "../../../assets/imgs/AcademyLogo/ansys-logo.jpg";
import MidasLogo from "../../../assets/imgs/AcademyLogo/midas_logo.png";
import WadhwaniLogo from "../../../assets/imgs/AcademyLogo/Wadhwani-Foundation-Logo.webp";

const getLogo = (title) => {
  if (title?.toLowerCase()?.includes("alteryx")) return AlteryxLogo;
  if (title?.toLowerCase()?.includes("aws") || title?.toLowerCase()?.includes("amazon")) return AWSLogo;
  if (title?.toLowerCase()?.includes("bentley")) return BentleyLogo;
  if (title?.toLowerCase()?.includes("blueprism") || title?.toLowerCase()?.includes("blue prism")) return BluePrismLogo;
  if (title?.toLowerCase()?.includes("celonis")) return CelonisLogo;
  if (title?.toLowerCase()?.includes("fortinet")) return FortinetLogo;
  if (title?.toLowerCase()?.includes("google")) return GoogleLogo;
  if (title?.toLowerCase()?.includes("juniper")) return JuniperLogo;
  if (title?.toLowerCase()?.includes("microchip")) return MicrochipLogo;
  if (title?.toLowerCase()?.includes("paloalto") || title?.toLowerCase()?.includes("palo alto")) return PaloAltoLogo;
  if (title?.toLowerCase()?.includes("uipath")) return UiPathLogo;
  if (title?.toLowerCase()?.includes("zscaler")) return ZscalerLogo;
  if (title?.toLowerCase()?.includes("altair")) return AltairLogo;
  if (title?.toLowerCase()?.includes("ansys")) return AnsycLogo;
  if (title?.toLowerCase()?.includes("eduskills")) return EduSkillsLogo;
  if (title?.toLowerCase()?.includes("midas")) return MidasLogo;
  if (title?.toLowerCase()?.includes("wadhwani")) return WadhwaniLogo;
  return null;
};

/* ─── Main Component ─── */


const CoeAdmin = () => {
  const [corporateList, setCorporateList] = useState([]);
  const [totalDomainsCount, setTotalDomainsCount] = useState("—");
  const [totalCoesCount, setTotalCoesCount] = useState("—");
  const [loading, setLoading] = useState(true);
  const [allAcademiesList, setAllAcademiesList] = useState([]);
  const [allAcademiesRaw, setAllAcademiesRaw] = useState([]);
  const navigate = useNavigate();

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedCorp, setSelectedCorp] = useState("");
  const [corpDomains, setCorpDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [coeName, setCoeName] = useState("");

  // Dynamic custom sections
  const [customSections, setCustomSections] = useState([]);
  const customRefs = useRef({});

  // Add Corporate Modal state
  const [openAddCorpModal, setOpenAddCorpModal] = useState(false);
  const [newCorpName, setNewCorpName] = useState("");
  const [customCorpName, setCustomCorpName] = useState("");
  const [newCorpBrand, setNewCorpBrand] = useState("");
  const [addingCorp, setAddingCorp] = useState(false);

  useEffect(() => { fetchCorporates(); }, []);

  useEffect(() => {
    if (selectedCorp) {
      CoeService.getDomainsByCorporate(selectedCorp)
        .then(res => { if (res.data) setCorpDomains(res.data); })
        .catch(err => console.error(err));
    } else {
      setCorpDomains([]);
    }
    setSelectedDomain("");
  }, [selectedCorp]);

  // Tracking IDs removed as requested - visibility will be based on document count instead.

  const fetchCorporates = async () => {
    try {
      setLoading(true);
      const [res, allRes] = await Promise.all([
        CoeService.getActiveAcademies(),
        CoeService.getAllAcademies()
      ]);

      // Handle the dashboard cards (academies WITH coe docs)
      if (res.data && Array.isArray(res.data)) {
        let domCnt = 0;
        const corps = res.data.map(corp => {
          domCnt += corp.domain_count || 0;
          return {
            ...corp,
            academy_name: corp.academy_brand || corp.academy_name,
          };
        });

        // ── 5-Minute Grace Period: Show newly created empty corporates for 5 mins ──
        const GRACE_MS = 5 * 60 * 1000; // 5 minutes
        const now = Date.now();
        const raw = localStorage.getItem("coe_new_corps");
        const newCorps = raw ? JSON.parse(raw) : [];
        // Filter out expired entries from localStorage
        const stillValid = newCorps.filter(c => (now - c.created_at) < GRACE_MS);
        localStorage.setItem("coe_new_corps", JSON.stringify(stillValid));
        // Add valid new corporates that aren't already in the active list
        const activeIds = new Set(corps.map(c => c.academy_id));
        stillValid.forEach(nc => {
          if (!activeIds.has(nc.academy_id)) {
            corps.unshift({ academy_id: nc.academy_id, academy_name: nc.academy_name, academy_brand: nc.academy_name, domain_count: 0, coe_count: 0 });
          }
        });
        // ── End Grace Period Logic ──

        setCorporateList(corps);
        setTotalDomainsCount(domCnt);
      }

      // Handle the complete list of active academies for the dropdown
      if (allRes.data && Array.isArray(allRes.data)) {
        setAllAcademiesRaw(allRes.data);
        const uniqueNames = [];
        allRes.data.forEach(a => {
          const name = (a.academy_brand || a.academy_name || "").trim();
          if (name && name !== "Corporate") {
            const rawName = name.toLowerCase().replace(/\s+/g, "");
            
            const existingIdx = uniqueNames.findIndex(ex => {
              const exRaw = ex.toLowerCase().replace(/\s+/g, "");
              return exRaw === rawName || exRaw.startsWith(rawName) || rawName.startsWith(exRaw);
            });

            if (existingIdx >= 0) {
              // We have a match! Keep the one that is longer or better formatted (has spaces)
              const existingName = uniqueNames[existingIdx];
              if (name.length > existingName.length || (name.includes(" ") && !existingName.includes(" "))) {
                uniqueNames[existingIdx] = name;
              }
            } else {
              uniqueNames.push(name);
            }
          }
        });
        setAllAcademiesList(uniqueNames.sort());
      }
    } catch (error) {
      console.error("Failed to fetch corporates", error);
    } finally {
      setLoading(false);
    }
  };


  // ── Groups (parent category + sub-items) ──
  const [groups, setGroups] = useState([]);
  const subItemRefs = useRef({});

  const addCustomSection = () => {
    const id = Date.now();
    setCustomSections(prev => [...prev, { id, name: "", files: [] }]);
    customRefs.current[id] = React.createRef();
  };

  const removeCustomSection = (id) => {
    setCustomSections(prev => prev.filter(s => s.id !== id));
    delete customRefs.current[id];
  };

  const updateSectionName = (id, name) =>
    setCustomSections(prev => prev.map(s => s.id === id ? { ...s, name } : s));

  const updateSectionFiles = (id, newFiles) =>
    setCustomSections(prev => prev.map(s => s.id === id ? { ...s, files: [...s.files, ...newFiles] } : s));

  const removeSectionFile = (sectionId, fileIndex) =>
    setCustomSections(prev => prev.map(s => s.id === sectionId ? { ...s, files: s.files.filter((_, i) => i !== fileIndex) } : s));

  const resetModal = () => {
    setSelectedCorp("");
    setSelectedDomain("");
    setCoeName("");
    setCustomSections([]);
    setGroups([]);
    setUploadProgress("");
    customRefs.current = {};
    subItemRefs.current = {};
  };

  // ── Group handlers ──
  const addGroup = () => {
    const id = Date.now();
    setGroups(prev => [...prev, { id, name: "", subItems: [] }]);
  };
  const removeGroup = (gid) => setGroups(prev => prev.filter(g => g.id !== gid));
  const updateGroupName = (gid, name) => setGroups(prev => prev.map(g => g.id === gid ? { ...g, name } : g));

  const addSubItem = (gid) => {
    const sid = Date.now();
    subItemRefs.current[sid] = React.createRef();
    setGroups(prev => prev.map(g => g.id === gid ? { ...g, subItems: [...g.subItems, { id: sid, name: "", files: [] }] } : g));
  };
  const removeSubItem = (gid, sid) => {
    setGroups(prev => prev.map(g => g.id === gid ? { ...g, subItems: g.subItems.filter(s => s.id !== sid) } : g));
    delete subItemRefs.current[sid];
  };
  const updateSubItemName = (gid, sid, name) =>
    setGroups(prev => prev.map(g => g.id === gid ? { ...g, subItems: g.subItems.map(s => s.id === sid ? { ...s, name } : s) } : g));
  const updateSubItemFiles = (gid, sid, newFiles) =>
    setGroups(prev => prev.map(g => g.id === gid ? { ...g, subItems: g.subItems.map(s => s.id === sid ? { ...s, files: [...s.files, ...newFiles] } : s) } : g));
  const removeSubItemFile = (gid, sid, idx) =>
    setGroups(prev => prev.map(g => g.id === gid ? { ...g, subItems: g.subItems.map(s => s.id === sid ? { ...s, files: s.files.filter((_, i) => i !== idx) } : s) } : g));

  const uploadSectionFile = async (file, docType, parentId) => {
    const domainObj = corpDomains.find(d => String(d.domain_id) === String(selectedDomain));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("academy_id", selectedCorp);
    formData.append("domain_id", selectedDomain);
    formData.append("domain_name", domainObj?.domain_name || "");
    formData.append("doc_type", docType);
    formData.append("doc_title", file.name);
    formData.append("doc_link", "");
    if (parentId) formData.append("parent_id", parentId);
    await CoeService.uploadDocument(formData);
  };

  const handleSave = async () => {
    if (!selectedCorp || !selectedDomain || !coeName.trim()) {
      alert("Please select a Corporate, Domain, and enter a COE name.");
      return;
    }
    const hasAtLeastOneFile = customSections.some(s => s.files.length > 0)
      || groups.some(g => g.subItems.some(s => s.files.length > 0));
    if (!hasAtLeastOneFile) {
      alert("Please upload at least one file in a section or group.");
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: Create COE container
      setUploadProgress("Creating COE container...");
      const domainObj = corpDomains.find(d => String(d.domain_id) === String(selectedDomain));
      const containerRes = await CoeService.addDocument({
        academy_id: parseInt(selectedCorp),
        domain_id: parseInt(selectedDomain),
        domain_name: domainObj?.domain_name || "",
        doc_type: "coe_content",
        doc_title: coeName.trim(),
        doc_link: ""
      });
      const parentId = containerRes.data?.coe_id || containerRes.data?.id || null;

      // Step 2: Flat sections
      for (const section of customSections) {
        if (section.files.length > 0 && section.name.trim()) {
          const docType = section.name.trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
          for (let i = 0; i < section.files.length; i++) {
            const file = section.files[i];
            setUploadProgress(`Uploading ${section.name} (${i + 1}/${section.files.length}): ${file.name}`);
            await uploadSectionFile(file, docType, parentId);
          }
        }
      }

      // Step 3: Groups (parent category → sub-categories)
      for (const group of groups) {
        if (!group.name.trim()) continue;
        const parentSlug = group.name.trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
        const domainObj2 = corpDomains.find(d => String(d.domain_id) === String(selectedDomain));
        // Create parent [CATEGORY] doc
        setUploadProgress(`Creating group "${group.name}"...`);
        await CoeService.addDocument({
          academy_id: parseInt(selectedCorp),
          domain_id: parseInt(selectedDomain),
          domain_name: domainObj2?.domain_name || "",
          doc_type: parentSlug,
          doc_title: `[CATEGORY] ${group.name.trim()}`,
          doc_link: "",
          parent_id: parentId
        });
        // Create each sub-item [CATEGORY] + upload its files
        for (const sub of group.subItems) {
          if (!sub.name.trim()) continue;
          const subSlug = `${parentSlug}_sub_${sub.name.trim().toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
          setUploadProgress(`Creating sub-category "${sub.name}"...`);
          await CoeService.addDocument({
            academy_id: parseInt(selectedCorp),
            domain_id: parseInt(selectedDomain),
            domain_name: domainObj2?.domain_name || "",
            doc_type: subSlug,
            doc_title: `[CATEGORY] ${sub.name.trim()}`,
            doc_link: "",
            parent_id: parentId
          });
          for (let i = 0; i < sub.files.length; i++) {
            const file = sub.files[i];
            setUploadProgress(`Uploading "${sub.name}" (${i + 1}/${sub.files.length}): ${file.name}`);
            await uploadSectionFile(file, subSlug, parentId);
          }
        }
      }
      setUploadProgress("");
      alert("COE documents uploaded successfully!");
      setOpenModal(false);
      resetModal();
    } catch (err) {
      console.error(err);
      setUploadProgress("");
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCorporate = async () => {
    const finalCorpName = newCorpName === "Other" ? customCorpName : newCorpName;

    if (!finalCorpName.trim()) {
      alert("Please select or enter a Corporate Name.");
      return;
    }
    setAddingCorp(true);
    try {
      let academyId = null;
      
      // If they selected an existing corporate, find its existing ID from the database
      if (newCorpName !== "Other") {
        const dropRaw = finalCorpName.toLowerCase().replace(/\s+/g, "");
        const existing = allAcademiesRaw.find(a => {
          const corpRaw = (a.academy_brand || a.academy_name || "").toLowerCase().replace(/\s+/g, "");
          return corpRaw === dropRaw || dropRaw.startsWith(corpRaw) || corpRaw.startsWith(dropRaw);
        });
        if (existing) {
          academyId = existing.academy_id;
        }
      }

      // Only create a NEW row in the database if it's a completely custom corporate
      if (!academyId) {
        const payload = {
          academy_name: finalCorpName.trim(),
          academy_brand: finalCorpName.trim(),
          mou_signed: new Date().toISOString().split("T")[0],
          mou_expired: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split("T")[0],
          is_status: "1"
        };
        const res = await CoeService.addCorporateAcademy(payload);
        academyId = res?.data?.academy_id;
      }

      if (!academyId) throw new Error("Could not determine or create academy_id.");

      // ACTUALLY SAVE THE CORPORATE TO THE MAPPING TABLE
      await CoeService.addCoeCorporate(academyId);

      // Close modal and reset fields.
      setOpenAddCorpModal(false);
      setNewCorpName("");
      setCustomCorpName("");
      setNewCorpBrand("");

      // Optimistically add the new corporate to the list so it appears immediately.
      // Also save to localStorage so it persists for 5 minutes across refreshes.
      const newCorp = {
        academy_id: academyId,
        academy_name: finalCorpName.trim(),
        academy_brand: finalCorpName.trim(),
      };
      const raw = localStorage.getItem("coe_new_corps");
      const saved = raw ? JSON.parse(raw) : [];
      saved.push({ academy_id: academyId, academy_name: finalCorpName.trim(), created_at: Date.now() });
      localStorage.setItem("coe_new_corps", JSON.stringify(saved));
      setCorporateList(prev => [newCorp, ...prev]);

      // Do NOT navigate away — admin stays on the corporates page,
      // sees the new card, and clicks it to enter the domains page themselves.
    } catch (err) {
      console.error(err);
      alert("Failed to add Corporate Academy.");
    } finally {
      setAddingCorp(false);
    }
  };

  // ── Helper: get initials from name ──
  const getInitials = (name) => {
    const words = (name || "").trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // ── Helper: get avatar color palette based on name ──
  const avatarPalette = (name) => {
    const n = name?.toLowerCase() || "";
    if (n.includes("aws") || n.includes("amazon"))    return { bg: "#fef3c7", color: "#b45309" };
    if (n.includes("microchip"))                       return { bg: "#e0e7ff", color: "#4338ca" };
    if (n.includes("juniper"))                         return { bg: "#d1fae5", color: "#065f46" };
    if (n.includes("google"))                          return { bg: "#fce7f3", color: "#9d174d" };
    if (n.includes("uipath"))                          return { bg: "#d1fae5", color: "#047857" };
    if (n.includes("eduskills"))                       return { bg: "#ede9fe", color: "#6d28d9" };
    if (n.includes("palo alto") || n.includes("paloalto")) return { bg: "#fee2e2", color: "#b91c1c" };
    if (n.includes("fortinet"))                        return { bg: "#fff7ed", color: "#c2410c" };
    if (n.includes("zscaler"))                         return { bg: "#e0f2fe", color: "#0369a1" };
    if (n.includes("bentley"))                         return { bg: "#fef9c3", color: "#92400e" };
    if (n.includes("alteryx"))                         return { bg: "#fce7f3", color: "#be185d" };
    if (n.includes("blue prism") || n.includes("blueprism")) return { bg: "#e0f2fe", color: "#0369a1" };
    if (n.includes("celonis"))                         return { bg: "#ecfdf5", color: "#15803d" };
    if (n.includes("ansys"))                           return { bg: "#fff7ed", color: "#c2410c" };
    if (n.includes("altair"))                          return { bg: "#fef3c7", color: "#92400e" };
    if (n.includes("midas"))                           return { bg: "#f0fdf4", color: "#166534" };
    if (n.includes("wadhwani"))                        return { bg: "#f5f3ff", color: "#7c3aed" };
    // fallback: hash-based color
    const colors = [
      { bg: "#e0f2fe", color: "#0369a1" }, { bg: "#fce7f3", color: "#9d174d" },
      { bg: "#d1fae5", color: "#065f46" }, { bg: "#fef3c7", color: "#b45309" },
      { bg: "#ede9fe", color: "#6d28d9" }, { bg: "#fee2e2", color: "#b91c1c" },
    ];
    const idx = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  };

  // ── Helper: subtitle tag for corporate ──
  const getSubtitle = (name) => {
    const n = name?.toLowerCase() || "";
    if (n.includes("aws") || n.includes("amazon"))    return "Cloud Technology";
    if (n.includes("microchip"))                       return "Semiconductors";
    if (n.includes("juniper"))                         return "Networking";
    if (n.includes("google"))                          return "Software & AI";
    if (n.includes("uipath"))                          return "Automation & RPA";
    if (n.includes("eduskills"))                       return "EdTech";
    if (n.includes("palo alto") || n.includes("paloalto")) return "Cybersecurity";
    if (n.includes("fortinet"))                        return "Network Security";
    if (n.includes("zscaler"))                         return "Cloud Security";
    if (n.includes("bentley"))                         return "Infrastructure";
    if (n.includes("alteryx"))                         return "Data Analytics";
    if (n.includes("blue prism") || n.includes("blueprism")) return "Intelligent Automation";
    if (n.includes("celonis"))                         return "Process Mining";
    if (n.includes("ansys"))                           return "Simulation";
    if (n.includes("altair"))                          return "Engineering";
    if (n.includes("midas"))                           return "Civil Engineering";
    if (n.includes("wadhwani"))                        return "AI & Skilling";
    return "Technology";
  };

  return (
    <Box sx={{ p: 4, minHeight: "100vh", bgcolor: "#f8f7f4" }}>

      {/* ── Page Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="800" sx={{ color: "#2563eb", letterSpacing: -0.5, fontSize: "2rem" }}>Center of Excellence (COE)</Typography>
          <Typography variant="body2" sx={{ color: "#888", mt: 0.5 }}>Select a corporate to view its domains</Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddCorpModal(true)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: "700", fontSize: "13px", bgcolor: "#2563eb", color: "#ffffff", boxShadow: "none", "&:hover": { bgcolor: "#1d4ed8", boxShadow: "0 4px 12px rgba(37,99,235,0.2)" } }}
          >
            Add Corporate
          </Button>

          {/* ── Stats ── */}
          <Box display="flex" gap={1.5}>
            {[
              { label: "Total Corporates", value: loading ? "..." : corporateList.length },
              { label: "Active Domains", value: loading ? "..." : totalDomainsCount },
            ].map((stat) => (
              <Box key={stat.label} sx={{ bgcolor: "#f0ede8", borderRadius: 2, px: 2, py: 1.5, minWidth: "110px" }}>
                <Typography sx={{ fontSize: "9px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>{stat.label}</Typography>
                <Typography sx={{ fontSize: "20px", fontWeight: "800", color: "#1a1a1a", lineHeight: 1 }}>{stat.value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Corporate Cards ── */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
          <CircularProgress sx={{ color: "#1a1a1a" }} />
        </Box>
      ) : corporateList.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10, color: "#aaa" }}>
          <Typography fontWeight={600}>No corporates added yet. Click "Add Corporate" to get started.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2.5 }}>
          {corporateList.map((corporate) => {
            const palette = avatarPalette(corporate.academy_name);
            const initials = getInitials(corporate.academy_name);
            const subtitle = getSubtitle(corporate.academy_name);
            return (
              <Box key={corporate.academy_id}>
                <Box
                  onClick={() => navigate(`/admin-coe/${corporate.academy_id}`)}
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 3,
                    aspectRatio: "1",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                  }}
                >
                  {/* Logo box — fills available space */}
                  <Box sx={{
                    width: "100%", flex: 1,
                    bgcolor: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden",
                    mb: 1,
                  }}>
                    {getLogo(corporate.academy_name) ? (
                      <img
                        src={getLogo(corporate.academy_name)}
                        alt={corporate.academy_name}
                        style={{ width: "80%", height: "80%", objectFit: "contain" }}
                      />
                    ) : (
                      <Typography sx={{ fontWeight: "800", fontSize: "20px", color: palette.color }}>
                        {initials}
                      </Typography>
                    )}
                  </Box>



                  {/* Open button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin-coe/${corporate.academy_id}`); }}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontWeight: "600",
                      fontSize: "12px", color: "#374151", borderColor: "#d1d5db",
                      py: 0.75, "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
                    }}
                  >
                    Open ›
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ─── Add Corporate Modal ─── */}
      <Dialog
        open={openAddCorpModal}
        onClose={() => !addingCorp && setOpenAddCorpModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: "#f8f9fa" } }}
      >
        <DialogTitle sx={{ fontWeight: "800", fontSize: "18px", color: "#1e293b", pb: 1 }}>
          Create New Corporate
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2, bgcolor: "#fff", borderRadius: 1.5 }}>
            <InputLabel>Corporate Name</InputLabel>
            <Select
              label="Corporate Name"
              value={newCorpName}
              onChange={(e) => setNewCorpName(e.target.value)}
              disabled={addingCorp}
            >
              {allAcademiesList
                .filter(name => {
                  const dropRaw = name.toLowerCase().replace(/\s+/g, "");
                  return !corporateList.some(corp => {
                    const corpRaw = (corp.academy_name || "").toLowerCase().replace(/\s+/g, "");
                    return corpRaw === dropRaw || dropRaw.startsWith(corpRaw) || corpRaw.startsWith(dropRaw);
                  });
                })
                .map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpenAddCorpModal(false)} disabled={addingCorp} sx={{ color: "#64748b", fontWeight: "bold" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddCorporate} 
            disabled={addingCorp || (!newCorpName.trim() && !customCorpName.trim())} 
            variant="contained" 
            sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" }, fontWeight: "bold", borderRadius: 2 }}
          >
            {addingCorp ? <CircularProgress size={24} sx={{ color: "#fff" }}/> : "Save Corporate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── COE Document Upload Modal ─── */}
      <Dialog
        open={openModal}
        onClose={() => !submitting && (setOpenModal(false), resetModal())}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: "#f8f9fa" } }}
      >
        <DialogTitle sx={{ fontWeight: "800", fontSize: "18px", color: "#1e293b", pb: 1 }}>
          COE Document Upload
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {/* Corporate + Domain side by side */}
          <Box display="flex" gap={2} mb={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Corporate</InputLabel>
              <Select value={selectedCorp} label="Select Corporate" onChange={(e) => setSelectedCorp(e.target.value)} disabled={submitting} sx={{ bgcolor: "#fff", borderRadius: 1.5 }}>
                {corporateList.map(corp => (
                  <MenuItem key={corp.academy_id} value={corp.academy_id}>{corp.academy_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!selectedCorp || corpDomains.length === 0 || submitting}>
              <InputLabel>Select Domain</InputLabel>
              <Select value={selectedDomain} label="Select Domain" onChange={(e) => setSelectedDomain(e.target.value)} sx={{ bgcolor: "#fff", borderRadius: 1.5 }}>
                {corpDomains.map(d => (
                  <MenuItem key={d.domain_id} value={d.domain_id}>{d.domain_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* COE Name */}
          <TextField
            size="small"
            fullWidth
            label="COE Name"
            placeholder="e.g. Juniper Networking COE"
            value={coeName}
            onChange={(e) => setCoeName(e.target.value)}
            disabled={submitting}
            sx={{ mb: 3, bgcolor: "#fff", borderRadius: 1.5 }}
          />

          {/* Custom sections */}
          {customSections.map((section) => {
            if (!customRefs.current[section.id]) customRefs.current[section.id] = React.createRef();
            const ref = customRefs.current[section.id];
            return (
              <Box key={section.id} mb={2} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 2.5, bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <TextField
                    size="small"
                    placeholder="Section name..."
                    value={section.name}
                    onChange={(e) => updateSectionName(section.id, e.target.value)}
                    disabled={submitting}
                    sx={{ flex: 1, "& .MuiInputBase-root": { fontSize: "14px", fontWeight: 700 } }}
                  />
                  <IconButton size="small" onClick={() => removeCustomSection(section.id)} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box
                  onClick={() => ref.current?.click()}
                  sx={{ border: "1.5px dashed #cbd5e1", borderRadius: 1.5, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", bgcolor: "#f8fafc", "&:hover": { bgcolor: "#f0f7ff", borderColor: "#93c5fd" } }}
                >
                  <Typography fontSize="13px" color="#94a3b8" fontWeight="500">Click to upload files</Typography>
                  <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); ref.current?.click(); }}
                    sx={{ textTransform: "none", fontSize: "12px", borderColor: "#cbd5e1", color: "#64748b", "&:hover": { borderColor: "#2563eb", color: "#2563eb" } }}>
                    Browse
                  </Button>
                </Box>
                <input type="file" multiple ref={ref} style={{ display: "none" }}
                  onChange={(e) => updateSectionFiles(section.id, Array.from(e.target.files))} />

                {section.files.length > 0 ? (
                  <Box mt={1.5} display="flex" flexDirection="column" gap={0.75}>
                    {section.files.map((file, idx) => (
                      <Box key={idx} display="flex" alignItems="center" gap={1} sx={{ bgcolor: "#eff6ff", borderRadius: 1, px: 1.5, py: 0.75 }}>
                        <FolderOpenIcon sx={{ fontSize: 15, color: "#3b82f6" }} />
                        <Typography fontSize="11px" color="#2563eb" fontWeight="600" flex={1} noWrap>{file.name}</Typography>
                        <IconButton size="small" onClick={() => removeSectionFile(section.id, idx)} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography fontSize="11px" color="#94a3b8" mt={1} fontWeight="500">No files uploaded yet.</Typography>
                )}
              </Box>
            );
          })}

          {/* + Add section */}
          <Button
            fullWidth variant="outlined" onClick={addCustomSection} disabled={submitting}
            sx={{ borderStyle: "dashed", borderColor: "#cbd5e1", color: "#64748b", borderRadius: 2, py: 1.5, fontWeight: "700", fontSize: "13px", textTransform: "none", mb: 2, "&:hover": { borderColor: "#2563eb", color: "#2563eb", bgcolor: "#eff6ff" } }}
          >
            + Add Resource Section
          </Button>

          {/* Groups (parent + sub-items) */}
          {groups.map((group) => (
            <Box key={group.id} mb={2} sx={{ border: "1.5px solid #f59e0b", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 4px rgba(245,158,11,0.08)" }}>
              {/* Group header */}
              <Box sx={{ bgcolor: "#fffbeb", px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #fde68a" }}>
                <TextField
                  size="small"
                  placeholder="Group name (e.g. Board Description)..."
                  value={group.name}
                  onChange={(e) => updateGroupName(group.id, e.target.value)}
                  disabled={submitting}
                  sx={{ flex: 1, "& .MuiInputBase-root": { fontWeight: 700, fontSize: "13px", bgcolor: "#fff", borderRadius: 1 } }}
                />
                <IconButton size="small" onClick={() => removeGroup(group.id)} sx={{ color: "#f59e0b", "&:hover": { color: "#ef4444" } }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Sub-items */}
              <Box sx={{ p: 2, bgcolor: "#fff" }}>
                {group.subItems.map((sub) => {
                  if (!subItemRefs.current[sub.id]) subItemRefs.current[sub.id] = React.createRef();
                  const subRef = subItemRefs.current[sub.id];
                  return (
                    <Box key={sub.id} mb={1.5} sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 2, bgcolor: "#fafafa" }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <TextField
                          size="small"
                          placeholder="Sub-item name (e.g. Board 1)..."
                          value={sub.name}
                          onChange={(e) => updateSubItemName(group.id, sub.id, e.target.value)}
                          disabled={submitting}
                          sx={{ flex: 1, "& .MuiInputBase-root": { fontSize: "12px", fontWeight: 600 } }}
                        />
                        <IconButton size="small" onClick={() => removeSubItem(group.id, sub.id)} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                          <CloseIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                      <Box
                        onClick={() => subRef.current?.click()}
                        sx={{ border: "1.5px dashed #e2e8f0", borderRadius: 1, p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", bgcolor: "#fff", "&:hover": { bgcolor: "#f0f7ff", borderColor: "#93c5fd" } }}
                      >
                        <Typography fontSize="12px" color="#94a3b8" fontWeight="500">Click to upload files</Typography>
                        <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); subRef.current?.click(); }}
                          sx={{ textTransform: "none", fontSize: "11px", borderColor: "#cbd5e1", color: "#64748b", py: 0.25 }}>
                          Browse
                        </Button>
                      </Box>
                      <input type="file" multiple ref={subRef} style={{ display: "none" }}
                        onChange={(e) => updateSubItemFiles(group.id, sub.id, Array.from(e.target.files))} />
                      {sub.files.length > 0 ? (
                        <Box mt={1} display="flex" flexDirection="column" gap={0.5}>
                          {sub.files.map((file, idx) => (
                            <Box key={idx} display="flex" alignItems="center" gap={1} sx={{ bgcolor: "#eff6ff", borderRadius: 1, px: 1.5, py: 0.5 }}>
                              <FolderOpenIcon sx={{ fontSize: 13, color: "#3b82f6" }} />
                              <Typography fontSize="11px" color="#2563eb" fontWeight="600" flex={1} noWrap>{file.name}</Typography>
                              <IconButton size="small" onClick={() => removeSubItemFile(group.id, sub.id, idx)} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" }, p: 0.25 }}>
                                <CloseIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography fontSize="10px" color="#94a3b8" mt={0.5} fontWeight="500">No files uploaded yet.</Typography>
                      )}
                    </Box>
                  );
                })}

                {/* + Add sub-item */}
                <Button
                  fullWidth size="small" onClick={() => addSubItem(group.id)} disabled={submitting}
                  sx={{ borderStyle: "dashed", borderColor: "#fde68a", color: "#b45309", borderRadius: 1.5, py: 1, fontWeight: "700", fontSize: "12px", textTransform: "none", mt: 0.5, "&:hover": { borderColor: "#f59e0b", bgcolor: "#fffbeb" } }}
                >
                  + Add sub-item
                </Button>
              </Box>
            </Box>
          ))}

          {/* + Add group */}
          <Button
            fullWidth variant="outlined" onClick={addGroup} disabled={submitting}
            sx={{ borderStyle: "dashed", borderColor: "#fde68a", color: "#b45309", borderRadius: 2, py: 1.5, fontWeight: "700", fontSize: "13px", textTransform: "none", mb: 1, "&:hover": { borderColor: "#f59e0b", color: "#92400e", bgcolor: "#fffbeb" } }}
          >
            + Add Module
          </Button>

          {/* Upload progress indicator */}
          {uploadProgress && (
            <Box display="flex" alignItems="center" gap={1.5} mt={1} px={1}>
              <CircularProgress size={14} thickness={5} sx={{ color: "#2563eb" }} />
              <Typography fontSize="12px" color="#2563eb" fontWeight="600">{uploadProgress}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setOpenModal(false); resetModal(); }} disabled={submitting} sx={{ color: "#64748b", fontWeight: "700", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave} variant="contained"
            disabled={submitting || !selectedCorp || !selectedDomain || !coeName.trim()}
            sx={{ fontWeight: "700", textTransform: "none", bgcolor: "#1e293b", "&:hover": { bgcolor: "#0f172a" }, borderRadius: 1.5, px: 3 }}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoeAdmin;
